import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import clsx from 'clsx';
import { AlertTriangle, CameraOff, Footprints, HeartPulse, Loader2, Printer } from 'lucide-react';
import { useAuthStore } from '../../../stores/authStore';
import { CHEMINS, groupesDeLaJournee, listesDeLaJournee } from '../../../lib/navigation/chemins';
import { lireAffectations, lireEssentiels, lireInscrits, lireJournees } from '../../../lib/jpa/gateway.supabase';
import { dateCourte, dateLongue, type InscritJpa, type JourneeJpa } from '../../../lib/jpa/regles';
import {
  classeCourte,
  classesDuGroupe,
  groupeProposePour,
  nomDeLaMoitie,
  proposerParAnnee,
  reprendreLesGroupes,
  scinderEnDeux,
  trierGroupes,
  trierParNom,
  type GroupePropose,
} from '../../../lib/jpa/groupes';
import {
  affecterInscriptions,
  appliquerRepartition,
  creerSousGroupe,
  lireSousGroupes,
  renommerSousGroupe,
  supprimerSousGroupe,
  type SousGroupe,
} from '../../../lib/groupes/groupes.supabase';
import { erreurNoms, normaliserNom, type GroupePlanifie } from '../../../lib/groupes/repartition';
import { indicateurs, type EssentielsDeLEnfant } from '../../../lib/presences/essentiels';

/*
  Les groupes d'une journée pédagogique, et leurs listes (demandé par Éclosion
  le 03/11/2026). L'équipe forme ses groupes elle-même : un clic propose un
  groupe par année (M1A et M1B ensemble, « Les M1 »), puis elle fusionne,
  scinde — une classe peut être séparée —, renomme et déplace des enfants. Le
  taux d'encadrement reste à l'équipe : rien ici ne le calcule.

  Les groupes sont les sous-groupes des stages : `subgroups` et
  `registrations.subgroup_id`, écrits avec les gestes de l'écran des
  sous-groupes (`lib/groupes`) ; aucune migration. Un enfant inscrit après la
  répartition reste « à répartir » jusqu'à ce qu'on le place ; on propose le
  groupe de ses camarades de classe.

  Les pictogrammes sont ceux des listes en ligne des stages, avec la même
  règle (`presences/essentiels.ts`) : santé à savoir, pas de photo — et ici,
  rentre seul.
*/

const icone = 'h-4 w-4 flex-shrink-0';
const A_REPARTIR = '__a_repartir__';

function Signes({ e }: { e: EssentielsDeLEnfant | undefined }) {
  const s = indicateurs(e);
  return (
    <>
      {s.sante && <HeartPulse className={`${icone} text-red-600`} aria-label="Santé : à savoir" />}
      {s.pasDePhoto && <CameraOff className={`${icone} text-amber-700`} aria-label="Pas de photo" />}
      {e?.depart.partSeul === true && <Footprints className={`${icone} text-green-700`} aria-label="Rentre seul" />}
    </>
  );
}

/* Une journée à la fois : changer de jour recharge tout, sans reste de l'autre. */
export default function AdminJourneeGroupesPage() {
  const { sessionId = '' } = useParams<{ sessionId: string }>();
  return <GroupesDeLaJournee key={sessionId} sessionId={sessionId} />;
}

function GroupesDeLaJournee({ sessionId }: { sessionId: string }) {
  const { user } = useAuthStore();
  const [journee, setJournee] = useState<JourneeJpa | null>(null);
  const [autresJours, setAutresJours] = useState<JourneeJpa[]>([]);
  const [inscrits, setInscrits] = useState<InscritJpa[]>([]);
  const [groupesLus, setGroupesLus] = useState<SousGroupe[]>([]);
  const [affectations, setAffectations] = useState<Map<string, string | null>>(new Map());
  const [essentiels, setEssentiels] = useState<Map<string, EssentielsDeLEnfant>>(new Map());
  const [groupesAilleurs, setGroupesAilleurs] = useState<Map<string, number>>(new Map());
  const [coches, setCoches] = useState<Set<string>>(new Set());
  const [chargement, setChargement] = useState(true);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [renomme, setRenomme] = useState<{ id: string; nom: string } | null>(null);
  const [nouveau, setNouveau] = useState<string | null>(null);
  const [cible, setCible] = useState('');

  const relireGroupes = useCallback(async () => {
    const [g, a] = await Promise.all([lireSousGroupes([sessionId]), lireAffectations(sessionId)]);
    setGroupesLus(g);
    setAffectations(a);
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    let annule = false;
    void (async () => {
      try {
        const toutes = await lireJournees();
        const j = toutes.find((x) => x.sessionId === sessionId) ?? null;
        if (!j) throw new Error('Cette journée est introuvable.');
        const lus = (await lireInscrits([sessionId])).filter((i) => i.vivante);
        /* Les autres journées du même centre, à une semaine près : le 5 et le 6 novembre. */
        const proche = (d: string) => Math.abs(Date.parse(`${d}T12:00:00Z`) - Date.parse(`${j.date}T12:00:00Z`)) <= 7 * 86400000;
        const autres = toutes
          .filter((x) => x.centreId === j.centreId && x.sessionId !== j.sessionId && proche(x.date))
          .sort((x, y) => x.date.localeCompare(y.date));
        const [g, a, e, ailleurs] = await Promise.all([
          lireSousGroupes([sessionId]),
          lireAffectations(sessionId),
          lireEssentiels(lus),
          lireSousGroupes(autres.map((x) => x.sessionId)),
        ]);
        if (annule) return;
        setJournee(j);
        setAutresJours(autres);
        setInscrits(trierParNom(lus));
        setGroupesLus(g);
        setAffectations(a);
        setEssentiels(e);
        setGroupesAilleurs(ailleurs.reduce((m, x) => m.set(x.sessionId, (m.get(x.sessionId) ?? 0) + 1), new Map<string, number>()));
      } catch (err) {
        if (!annule) setErreur(err instanceof Error ? err.message : String(err));
      } finally {
        if (!annule) setChargement(false);
      }
    })();
    return () => { annule = true; };
  }, [sessionId]);

  const groupes = useMemo(() => trierGroupes(groupesLus), [groupesLus]);
  const idsDesGroupes = useMemo(() => new Set(groupes.map((g) => g.id)), [groupes]);
  const groupeDe = useCallback(
    (registrationId: string) => {
      const g = affectations.get(registrationId) ?? null;
      return g && idsDesGroupes.has(g) ? g : null;
    },
    [affectations, idsDesGroupes],
  );
  const membres = (groupeId: string) => inscrits.filter((i) => groupeDe(i.registrationId) === groupeId);
  const aRepartir = inscrits.filter((i) => groupeDe(i.registrationId) === null);
  const propositions = aRepartir.map((i) => ({ inscrit: i, groupeId: groupeProposePour(i, inscrits, groupeDe) }));
  const nomDuGroupe = (id: string | null) => groupes.find((g) => g.id === id)?.nom ?? null;
  const parAnneePropose = useMemo(() => proposerParAnnee(inscrits), [inscrits]);

  /* Toute écriture : une à la fois, puis ce que dit la base, relu. */
  const agir = async (geste: () => Promise<void>) => {
    if (enCours) return;
    setEnCours(true);
    setErreur(null);
    try {
      await geste();
      await relireGroupes();
    } catch (err) {
      setErreur(err instanceof Error ? err.message : String(err));
      await relireGroupes().catch(() => undefined);
    } finally {
      setEnCours(false);
    }
  };

  const plan = (proposes: GroupePropose[]): GroupePlanifie[] => {
    const parId = new Map(inscrits.map((i) => [i.registrationId, i]));
    return proposes.map((g) => ({
      nom: normaliserNom(g.nom),
      enfants: g.inscriptionIds.flatMap((id) => {
        const i = parId.get(id);
        return i ? [{ inscriptionId: id, nom: i.nom, prenom: i.prenom, dateNaissance: null }] : [];
      }),
      ageMin: null,
      ageMax: null,
    }));
  };

  const parAnnee = () => {
    if (groupes.length > 0 && !window.confirm('Refaire les groupes par année ? Les groupes actuels et vos ajustements seront remplacés.')) return;
    void agir(async () => {
      await appliquerRepartition(sessionId, plan(parAnneePropose), groupesLus, user?.id ?? null);
      setCoches(new Set());
    });
  };

  const reprendre = (autre: JourneeJpa) => {
    if (groupes.length > 0 && !window.confirm(`Reprendre les groupes du ${dateCourte(autre.date)} ? Les groupes actuels seront remplacés.`)) return;
    void agir(async () => {
      const [gs, as, ins] = await Promise.all([
        lireSousGroupes([autre.sessionId]),
        lireAffectations(autre.sessionId),
        lireInscrits([autre.sessionId]),
      ]);
      const ids = new Set(gs.map((g) => g.id));
      const proposes = reprendreLesGroupes(inscrits, {
        inscrits: ins.filter((i) => i.vivante),
        groupes: trierGroupes(gs),
        groupeDe: (id) => {
          const g = as.get(id) ?? null;
          return g && ids.has(g) ? g : null;
        },
      });
      if (proposes.length === 0) throw new Error(`Aucun enfant de ce jour ne correspond aux groupes du ${dateCourte(autre.date)}.`);
      await appliquerRepartition(sessionId, plan(proposes), groupesLus, user?.id ?? null);
      setCoches(new Set());
    });
  };

  const placerLesNouveaux = () => void agir(async () => {
    const parGroupe = new Map<string, string[]>();
    for (const p of propositions) {
      if (p.groupeId) parGroupe.set(p.groupeId, [...(parGroupe.get(p.groupeId) ?? []), p.inscrit.registrationId]);
    }
    for (const [id, ids] of parGroupe) await affecterInscriptions(ids, { id, nom: nomDuGroupe(id) ?? '' });
  });

  const nomRefuse = (nom: string, sauf?: string) =>
    erreurNoms([...groupes.filter((g) => g.id !== sauf).map((g) => g.nom), nom]);

  const creer = (nom: string) => {
    const refus = nomRefuse(nom);
    if (refus) {
      setErreur(refus);
      return;
    }
    void agir(async () => {
      await creerSousGroupe(sessionId, nom, user?.id ?? null);
      setNouveau(null);
    });
  };

  const renommer = (id: string, nom: string) => {
    const refus = nomRefuse(nom, id);
    if (refus) {
      setErreur(refus);
      return;
    }
    void agir(async () => {
      await renommerSousGroupe(id, nom);
      setRenomme(null);
    });
  };

  const scinder = (g: SousGroupe) => void agir(async () => {
    const [, seconde] = scinderEnDeux(membres(g.id));
    if (seconde.length === 0) return;
    const cree = await creerSousGroupe(sessionId, nomDeLaMoitie(g.nom, groupes.map((x) => x.nom)), user?.id ?? null);
    await affecterInscriptions(seconde, { id: cree.id, nom: cree.nom });
  });

  const fusionner = (source: SousGroupe, cibleId: string) => {
    const vers = groupes.find((x) => x.id === cibleId);
    if (!vers) return;
    const n = membres(source.id).length;
    if (!window.confirm(`Mettre ${n === 1 ? 'l’enfant' : `les ${n} enfants`} de « ${source.nom} » dans « ${vers.nom} » ? « ${source.nom} » disparaît.`)) return;
    void agir(async () => {
      await affecterInscriptions(membres(source.id).map((i) => i.registrationId), { id: vers.id, nom: vers.nom });
      await supprimerSousGroupe(source.id);
    });
  };

  const supprimer = (g: SousGroupe) => {
    const n = membres(g.id).length;
    if (!window.confirm(`Supprimer « ${g.nom} » ?${n ? ` ${n === 1 ? 'Son enfant passe' : `Ses ${n} enfants passent`} « à répartir ».` : ''}`)) return;
    void agir(() => supprimerSousGroupe(g.id));
  };

  const deplacer = () => {
    const ids = [...coches];
    if (ids.length === 0 || !cible) return;
    void agir(async () => {
      if (cible === A_REPARTIR) {
        await affecterInscriptions(ids, null);
      } else {
        const vers = groupes.find((x) => x.id === cible);
        if (!vers) return;
        await affecterInscriptions(ids, { id: vers.id, nom: vers.nom });
      }
      setCoches(new Set());
      setCible('');
    });
  };

  const basculer = (ids: string[]) =>
    setCoches((avant) => {
      const apres = new Set(avant);
      const tous = ids.every((id) => apres.has(id));
      for (const id of ids) {
        if (tous) apres.delete(id);
        else apres.add(id);
      }
      return apres;
    });

  if (chargement) {
    return <div className="flex justify-center items-center min-h-[40vh]"><Loader2 className="h-8 w-8 animate-spin text-primary-600" /></div>;
  }
  if (!journee) {
    return (
      <div className="space-y-4">
        <p className="text-red-700">{erreur ?? 'Cette journée est introuvable.'}</p>
        <Link to={CHEMINS.planification.jpa} className="btn-outline">Retour aux journées</Link>
      </div>
    );
  }

  const listes = (liste: 'presence' | 'sante') => `${listesDeLaJournee(sessionId)}?par=groupe&liste=${liste}`;

  const ligneEnfant = (i: InscritJpa) => (
    <li key={i.registrationId}>
      <label className={clsx('flex min-h-[36px] cursor-pointer items-center gap-2 rounded px-1 text-sm', coches.has(i.registrationId) && 'bg-primary-50')}>
        <input
          type="checkbox"
          checked={coches.has(i.registrationId)}
          onChange={() => basculer([i.registrationId])}
          className="h-4 w-4 rounded border-gray-300 text-primary-600"
        />
        <span className="min-w-0 flex-1 truncate"><span className="font-semibold">{i.nom}</span> {i.prenom}</span>
        <Signes e={essentiels.get(i.registrationId)} />
        <span className="w-10 flex-shrink-0 text-right text-xs text-gray-500">{classeCourte(i.classe)}</span>
      </label>
    </li>
  );

  return (
    <div className="space-y-5 pb-28">
      <div>
        <Link to={`${CHEMINS.planification.jpa}?journee=${encodeURIComponent(sessionId)}`} className="text-sm font-semibold text-primary-700 hover:underline">
          ← Journées pédagogiques
        </Link>
        <div className="mt-2 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Groupes et listes de présence</h1>
            <p className="mt-1 text-gray-600">
              <span className="inline-block first-letter:uppercase">{dateLongue(journee.date)}</span> · {journee.ecole ?? journee.centre} ·{' '}
              {inscrits.length} inscrit{inscrits.length > 1 ? 's' : ''} · {groupes.length} groupe{groupes.length > 1 ? 's' : ''}
              {aRepartir.length > 0 && groupes.length > 0 && <> · <strong className="text-amber-800">{aRepartir.length} à répartir</strong></>}
            </p>
          </div>
          {autresJours.length > 0 && (
            <nav aria-label="Les journées proches" className="flex flex-wrap gap-2">
              {[journee, ...autresJours].sort((x, y) => x.date.localeCompare(y.date)).map((a) => (a.sessionId === journee.sessionId ? (
                <span key={a.sessionId} aria-current="page" className="inline-flex min-h-[40px] items-center rounded-lg border-2 border-primary-600 bg-primary-50 px-3 text-sm font-bold text-primary-800 first-letter:uppercase">
                  {dateCourte(a.date)}
                </span>
              ) : (
                <Link key={a.sessionId} to={groupesDeLaJournee(a.sessionId)} className="inline-flex min-h-[40px] items-center rounded-lg border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-700 first-letter:uppercase hover:border-primary-300">
                  {dateCourte(a.date)}
                </Link>
              )))}
            </nav>
          )}
        </div>
      </div>

      {erreur && (
        <div className="flex items-start gap-2 rounded-lg bg-red-50 p-4 text-red-700" role="alert">
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0" aria-hidden="true" />
          <p>{erreur}</p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white p-3">
        <button type="button" onClick={parAnnee} disabled={enCours || inscrits.length === 0} className={clsx(groupes.length === 0 ? 'btn-primary' : 'btn-outline', 'disabled:opacity-50')}>
          {groupes.length === 0 ? 'Proposer un groupe par année' : 'Refaire par année…'}
        </button>
        {autresJours.filter((a) => (groupesAilleurs.get(a.sessionId) ?? 0) > 0).map((a) => (
          <button key={a.sessionId} type="button" onClick={() => reprendre(a)} disabled={enCours || inscrits.length === 0} className="btn-outline disabled:opacity-50">
            Reprendre les groupes du {dateCourte(a.date)}
          </button>
        ))}
        {nouveau === null ? (
          <button type="button" onClick={() => setNouveau('')} disabled={enCours} className="btn-outline disabled:opacity-50">+ Nouveau groupe</button>
        ) : (
          <form className="flex flex-wrap items-center gap-2" onSubmit={(ev) => { ev.preventDefault(); creer(nouveau); }}>
            <input autoFocus value={nouveau} onChange={(ev) => setNouveau(ev.target.value)} placeholder="Nom du groupe" aria-label="Nom du nouveau groupe" className="form-input min-h-[40px] w-48" />
            <button type="submit" disabled={enCours || !nouveau.trim()} className="btn-primary disabled:opacity-50">Créer</button>
            <button type="button" onClick={() => setNouveau(null)} className="btn-outline">Annuler</button>
          </form>
        )}
        <span className="flex-1" />
        {enCours && <Loader2 className="h-5 w-5 animate-spin text-primary-600" aria-label="Enregistrement" />}
        <Link to={listes('presence')} className="btn-primary inline-flex items-center gap-2">
          <Printer className="h-4 w-4" aria-hidden="true" />
          Listes de présence
        </Link>
        <Link to={listes('sante')} className="btn-outline inline-flex items-center gap-2">
          <HeartPulse className="h-4 w-4" aria-hidden="true" />
          Listes santé
        </Link>
      </div>

      <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
        <span className="inline-flex items-center gap-1"><HeartPulse className={`${icone} text-red-600`} aria-hidden="true" /> santé : à savoir</span>
        <span className="inline-flex items-center gap-1"><CameraOff className={`${icone} text-amber-700`} aria-hidden="true" /> pas de photo</span>
        <span className="inline-flex items-center gap-1"><Footprints className={`${icone} text-green-700`} aria-hidden="true" /> rentre seul</span>
      </p>

      {inscrits.length === 0 && <p className="text-gray-600">Aucun inscrit pour cette journée.</p>}

      {groupes.length === 0 && inscrits.length > 0 && (
        <section aria-label="Proposition" className="space-y-2 rounded-xl border border-dashed border-primary-300 bg-primary-50 p-4">
          <h2 className="text-base font-bold text-primary-900">Aucun groupe pour l’instant. « Proposer un groupe par année » crée :</h2>
          <ul className="flex flex-wrap gap-2">
            {parAnneePropose.map((g) => {
              const parId = new Set(g.inscriptionIds);
              const classes = classesDuGroupe(inscrits.filter((i) => parId.has(i.registrationId)));
              return (
                <li key={g.nom} className="rounded-lg bg-white px-3 py-2 text-sm shadow-sm">
                  <strong>{g.nom}</strong> · {g.inscriptionIds.length}
                  <span className="block text-xs text-gray-600">{classes.map((c) => `${c.libelle} ${c.nombre}`).join(' · ')}</span>
                </li>
              );
            })}
          </ul>
          <p className="text-sm text-primary-900">Ensuite : fusionner, scinder, renommer, déplacer des enfants.</p>
        </section>
      )}

      {groupes.length > 0 && aRepartir.length > 0 && (
        <section aria-label="À répartir" className="space-y-2 rounded-xl border border-amber-300 bg-amber-50 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-base font-bold text-amber-900">
              {aRepartir.length} enfant{aRepartir.length > 1 ? 's' : ''} à répartir
              <span className="font-normal"> — inscrit{aRepartir.length > 1 ? 's' : ''} après la répartition, ou retiré{aRepartir.length > 1 ? 's' : ''} d’un groupe</span>
            </h2>
            {propositions.some((p) => p.groupeId) && (
              <button type="button" onClick={placerLesNouveaux} disabled={enCours} className="btn-primary disabled:opacity-50">
                Les placer avec leur classe
              </button>
            )}
          </div>
          <ul className="grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
            {propositions.map(({ inscrit: i, groupeId }) => (
              <li key={i.registrationId}>
                <label className={clsx('flex min-h-[36px] cursor-pointer items-center gap-2 rounded bg-white px-2 text-sm', coches.has(i.registrationId) && 'ring-2 ring-primary-400')}>
                  <input type="checkbox" checked={coches.has(i.registrationId)} onChange={() => basculer([i.registrationId])} className="h-4 w-4 rounded border-gray-300 text-primary-600" />
                  <span className="min-w-0 flex-1 truncate"><span className="font-semibold">{i.nom}</span> {i.prenom} · {classeCourte(i.classe)}</span>
                  <Signes e={essentiels.get(i.registrationId)} />
                  <span className="text-xs text-amber-900">{groupeId ? `→ ${nomDuGroupe(groupeId)}` : 'à placer'}</span>
                </label>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="grid items-start gap-4 [grid-template-columns:repeat(auto-fill,minmax(300px,1fr))]">
        {groupes.map((g) => {
          const leurs = membres(g.id);
          const classes = classesDuGroupe(leurs);
          return (
            <section key={g.id} aria-label={g.nom} className="overflow-hidden rounded-xl border border-gray-200 bg-white">
              <div className="space-y-2 border-b border-gray-100 p-3">
                {renomme?.id === g.id ? (
                  <form className="flex items-center gap-2" onSubmit={(ev) => { ev.preventDefault(); renommer(g.id, renomme.nom); }}>
                    <input autoFocus value={renomme.nom} onChange={(ev) => setRenomme({ id: g.id, nom: ev.target.value })} aria-label={`Nouveau nom de ${g.nom}`} className="form-input min-h-[40px] min-w-0 flex-1" />
                    <button type="submit" disabled={enCours || !renomme.nom.trim()} className="btn-primary disabled:opacity-50">OK</button>
                    <button type="button" onClick={() => setRenomme(null)} className="btn-outline">Annuler</button>
                  </form>
                ) : (
                  <div className="flex items-baseline justify-between gap-2">
                    <h2 className="text-lg font-extrabold">{g.nom}</h2>
                    <span className="text-2xl font-extrabold text-primary-700" aria-label={`${leurs.length} enfants`}>{leurs.length}</span>
                  </div>
                )}
                <div className="flex flex-wrap gap-1">
                  {classes.map((c) => {
                    const ids = leurs.filter((i) => (i.classeId ?? null) === c.classeId).map((i) => i.registrationId);
                    const toutes = ids.length > 0 && ids.every((id) => coches.has(id));
                    return (
                      <button
                        key={c.classeId ?? 'sans'}
                        type="button"
                        onClick={() => basculer(ids)}
                        aria-pressed={toutes}
                        title={`Choisir les ${c.nombre} enfants de ${c.libelle}`}
                        className={clsx('rounded-full px-2 py-0.5 text-xs font-bold', toutes ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-primary-50')}
                      >
                        {c.libelle} {c.nombre}
                      </button>
                    );
                  })}
                  {leurs.length === 0 && <span className="text-xs text-gray-500">Vide : y déplacer des enfants, ou le supprimer.</span>}
                </div>
              </div>
              <ul className="px-2 py-1">{leurs.map(ligneEnfant)}</ul>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-gray-100 px-3 py-2 text-sm">
                <button type="button" onClick={() => setRenomme({ id: g.id, nom: g.nom })} disabled={enCours} className="font-semibold text-primary-700 hover:underline disabled:opacity-50">Renommer</button>
                <button type="button" onClick={() => scinder(g)} disabled={enCours || leurs.length < 2} className="font-semibold text-primary-700 hover:underline disabled:opacity-50">Scinder</button>
                {groupes.length > 1 && leurs.length > 0 && (
                  <label className="inline-flex items-center">
                    <span className="sr-only">Fusionner {g.nom} avec un autre groupe</span>
                    <select
                      value=""
                      disabled={enCours}
                      onChange={(ev) => ev.target.value && fusionner(g, ev.target.value)}
                      className="rounded border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm font-semibold text-primary-700"
                    >
                      <option value="">Fusionner avec…</option>
                      {groupes.filter((x) => x.id !== g.id).map((x) => <option key={x.id} value={x.id}>{x.nom}</option>)}
                    </select>
                  </label>
                )}
                <span className="flex-1" />
                <button type="button" onClick={() => supprimer(g)} disabled={enCours} className="font-semibold text-gray-600 hover:text-red-700 disabled:opacity-50">Supprimer</button>
              </div>
            </section>
          );
        })}
      </div>

      {coches.size > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-gray-200 bg-white shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
          <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-3 px-4 py-3">
            <span className="font-bold">{coches.size} enfant{coches.size > 1 ? 's' : ''} choisi{coches.size > 1 ? 's' : ''}</span>
            <label className="flex items-center gap-2 text-sm">
              vers
              <select value={cible} onChange={(ev) => setCible(ev.target.value)} className="form-input min-h-[40px] w-56">
                <option value="">Choisir le groupe…</option>
                {groupes.map((g) => <option key={g.id} value={g.id}>{g.nom}</option>)}
                <option value={A_REPARTIR}>À répartir (sans groupe)</option>
              </select>
            </label>
            <button type="button" onClick={deplacer} disabled={enCours || !cible} className="btn-primary disabled:opacity-50">Déplacer</button>
            <button type="button" onClick={() => setCoches(new Set())} className="btn-outline">Annuler</button>
          </div>
        </div>
      )}
    </div>
  );
}
